import { useEffect, useMemo, useRef, useState } from "react";
import type {
  AdminNotificationPreview,
  AdminSendNotificationBody,
  ChainEntry,
  NotificationScreen,
  PortfolioWalletToken,
  PromoScreen,
} from "../api/types";
import {
  fetchLifiEstimate,
  formatAmount,
  formatBaseUnits,
  formatUsd,
  toBaseUnits,
  tokenAmountFromFiat,
  type EstimateFn,
  type SwapEstimate,
} from "../lib/swap-quote";
import { ErrorNotice } from "./Notice";
import { TokenLogo } from "./TokenLogo";
import { TokenPicker, type PickerToken } from "./TokenPicker";

export interface SendComposerProps {
  userId: string;
  /**
   * The destination allowlist, straight from GET /v1/admin/notification-screens.
   * Never a constant in this file: the API owns which screens are linkable, and
   * a hardcoded copy here would drift into 400s on the next contract change.
   */
  screens: NotificationScreen[];
  screensError?: unknown;
  /** The API chain catalog, the same one the app's swap pickers read. */
  chains: ChainEntry[];
  /** The member's wallet slice, for balances and the dollar price. */
  holdings: PortfolioWalletToken[];
  onPreview: (body: AdminSendNotificationBody) => Promise<AdminNotificationPreview>;
  onSend: (body: AdminSendNotificationBody) => Promise<void>;
  /** Injected so tests never reach LI.FI. */
  estimate?: EstimateFn;
}

type Step = "screen" | "amount" | "preview" | "sent";
type Denomination = "token" | "fiat";

/** One line under each destination, so the choice reads as where it lands. */
const SCREEN_HINT: Partial<Record<PromoScreen, string>> = {
  swap: "Opens the swap with tokens and an amount filled in",
  card: "Opens their card",
  vault: "Opens Earn",
};

const STEP_LABEL: Record<Exclude<Step, "sent">, string> = {
  screen: "Destination",
  amount: "Amount",
  preview: "Preview and send",
};

const tokenKey = (chainId: number, address: string) =>
  `${chainId}:${address.toLowerCase()}`;

/**
 * The catalog, joined with the member's balances and ordered the way the app's
 * pickers order it (largest holding first). Built from the catalog rather than
 * from holdings so a token the member does not hold yet can still be the buy leg.
 */
function pickerTokens(chains: ChainEntry[], holdings: PortfolioWalletToken[]): PickerToken[] {
  const held = new Map(holdings.map((h) => [tokenKey(h.chainId, h.address), h]));
  const rows = chains.flatMap((chain) =>
    chain.tokens.map((token) => {
      const holding = held.get(tokenKey(chain.id, token.address));
      const amount = Number(holding?.amount ?? 0);
      const usd = Number(holding?.usdValue ?? 0);
      return {
        key: tokenKey(chain.id, token.address),
        chainId: chain.id,
        chainName: chain.name,
        symbol: token.symbol,
        address: token.address,
        decimals: token.decimals,
        logoUrl: token.logoUrl || null,
        balance: holding?.amount ?? "0",
        price: amount > 0 && usd > 0 ? usd / amount : null,
        usdValue: Number.isFinite(usd) ? usd : 0,
      };
    }),
  );
  return rows.sort((a, b) => b.usdValue - a.usdValue);
}

/** The pair the app would open on: the largest holding, then a same-chain token. */
function defaultPair(tokens: PickerToken[]): [string, string] {
  const sell = tokens.find((t) => Number(t.balance) > 0) ?? tokens[0];
  if (!sell) return ["", ""];
  const buy =
    tokens.find((t) => t.chainId === sell.chainId && t.key !== sell.key) ??
    tokens.find((t) => t.key !== sell.key);
  return [sell.key, buy?.key ?? ""];
}

/**
 * Destination, then (for swap) the amount, then the preview and the send.
 * The API writes every word the member reads; the console only chooses where
 * the notification lands and, for swap, what it fills in.
 */
export function SendComposer({
  userId,
  screens,
  screensError,
  chains,
  holdings,
  onPreview,
  onSend,
  estimate = fetchLifiEstimate,
}: SendComposerProps) {
  const [step, setStep] = useState<Step>("screen");
  const [preset, setPreset] = useState<PromoScreen | null>(null);
  const [preview, setPreview] = useState<AdminNotificationPreview | null>(null);
  // The body the preview was rendered from. Send posts exactly this, so what
  // staff read is what the member gets.
  const [previewed, setPreviewed] = useState<AdminSendNotificationBody | null>(null);
  const [busy, setBusy] = useState<"preview" | "send" | null>(null);
  const [error, setError] = useState<unknown>(null);

  // --- swap legs --------------------------------------------------------------
  const tokens = useMemo(() => pickerTokens(chains, holdings), [chains, holdings]);
  const ambiguous = useMemo(() => ambiguousSymbols(tokens), [tokens]);
  const [sellKey, setSellKey] = useState("");
  const [buyKey, setBuyKey] = useState("");
  const [input, setInput] = useState("");
  const [denomination, setDenomination] = useState<Denomination>("token");
  const [picking, setPicking] = useState<"sell" | "buy" | null>(null);

  // Seed the pair once the catalog is here, and only while untouched.
  useEffect(() => {
    if (sellKey || tokens.length === 0) return;
    const [sell, buy] = defaultPair(tokens);
    setSellKey(sell);
    setBuyKey(buy);
  }, [tokens, sellKey]);

  const sell = tokens.find((t) => t.key === sellKey) ?? null;
  const buy = tokens.find((t) => t.key === buyKey) ?? null;
  // Without a price there is no dollar entry, same as the app.
  const denom: Denomination = sell?.price ? denomination : "token";
  const amount =
    !sell || input === ""
      ? ""
      : denom === "fiat"
        ? tokenAmountFromFiat(input, sell.price!, sell.decimals)
        : input;
  const amountValid = amount !== "" && validAmount(amount, sell?.decimals ?? 0);
  const shortfall = amountValid && sell ? Number(amount) > Number(sell.balance) : false;

  // --- estimate ---------------------------------------------------------------
  const [quote, setQuote] = useState<SwapEstimate | null>(null);
  const [quoteState, setQuoteState] = useState<"idle" | "loading" | "failed">("idle");
  useEffect(() => {
    setQuote(null);
    if (step !== "amount" || !sell || !buy || sell.key === buy.key || !amountValid) {
      setQuoteState("idle");
      return;
    }
    setQuoteState("loading");
    let cancelled = false;
    // Debounced like the app's quote, so typing "50" asks once, not twice.
    const timer = setTimeout(() => {
      estimate({
        fromChain: sell.chainId,
        toChain: buy.chainId,
        fromToken: sell.address,
        toToken: buy.address,
        fromAmount: toBaseUnits(amount, sell.decimals),
      })
        .then((q) => {
          if (cancelled) return;
          setQuote(q);
          setQuoteState("idle");
        })
        .catch(() => {
          if (!cancelled) setQuoteState("failed");
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [step, sell, buy, amount, amountValid, estimate]);

  // One composer, one in-flight request, ever. The disabled attribute is the
  // visible guard; this closes the window before React re-renders.
  const inFlight = useRef(false);

  async function requestPreview(body: AdminSendNotificationBody) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy("preview");
    setError(null);
    try {
      setPreview(await onPreview(body));
      setPreviewed(body);
      setStep("preview");
    } catch (err) {
      setError(err);
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  }

  async function send() {
    if (!previewed || inFlight.current) return;
    inFlight.current = true;
    setBusy("send");
    setError(null);
    try {
      await onSend(previewed);
      setStep("sent");
    } catch (err) {
      setError(err);
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  }

  function startOver() {
    setStep("screen");
    setPreset(null);
    setPreview(null);
    setPreviewed(null);
    setInput("");
    setError(null);
  }

  function pick(side: "sell" | "buy", key: string) {
    setPicking(null);
    const other = side === "sell" ? buyKey : sellKey;
    // Choosing the other leg's token flips the pair, as the app's picker does,
    // rather than leaving a same-token swap nobody can send.
    if (key === other) {
      setSellKey(buyKey);
      setBuyKey(sellKey);
    } else if (side === "sell") {
      setSellKey(key);
    } else {
      setBuyKey(key);
    }
    // The same number means a different amount of a different asset.
    if (side === "sell" || key === other) setInput("");
  }

  const swapBody: AdminSendNotificationBody | null =
    sell && buy && sell.key !== buy.key && amountValid
      ? {
          userId,
          preset: "swap",
          prefill: {
            sell: { chainId: sell.chainId, address: sell.address },
            buy: { chainId: buy.chainId, address: buy.address },
            amount,
          },
        }
      : null;

  // Card and Vault have nothing to fill in, so they go straight to preview.
  const stepIds: Exclude<Step, "sent">[] =
    preset === null || preset === "swap" ? ["screen", "amount", "preview"] : ["screen", "preview"];
  const current = step === "sent" ? stepIds.length : stepIds.indexOf(step);
  const screenLabel = screens.find((s) => s.key === preset)?.label ?? "";

  return (
    <div className="composer">
      {/* On a phone the row of steps wraps into a second line of connectors,
          so it shows only the current one, after this count. */}
      <ol
        className="stepper"
        aria-label="Steps"
        data-progress={step === "sent" ? undefined : `Step ${current + 1} of ${stepIds.length}`}
      >
        {stepIds.map((id, index) => {
          const state = index < current ? "done" : index === current ? "current" : "todo";
          const mark = (
            <span className={`step-mark ${state}`} aria-hidden="true">
              {state === "done" ? "✓" : index + 1}
            </span>
          );
          return (
            <li
              key={id}
              className={`stepper-item ${state}`}
              aria-current={state === "current" ? "step" : undefined}
            >
              {state === "done" && step !== "sent" ? (
                <button type="button" className="stepper-link" onClick={() => setStep(id)}>
                  {mark}
                  {STEP_LABEL[id]}
                </button>
              ) : (
                <span className="stepper-label">
                  {mark}
                  {STEP_LABEL[id]}
                </span>
              )}
            </li>
          );
        })}
      </ol>

      {step === "screen" ? (
        <div className="stack">
          <ErrorNotice error={screensError} />
          {screens.length === 0 ? (
            <span className="subtle">No screens available</span>
          ) : (
            <div className="choice-grid" role="group" aria-label="Destination">
              {screens.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className="choice"
                  aria-pressed={preset === s.key}
                  onClick={() => {
                    setPreset(s.key);
                    setError(null);
                  }}
                >
                  <span className="choice-title">{s.label}</span>
                  {SCREEN_HINT[s.key] ? (
                    <span className="choice-hint">{SCREEN_HINT[s.key]}</span>
                  ) : null}
                </button>
              ))}
            </div>
          )}
          <ErrorNotice error={error} />
          <div className="composer-actions">
            <span className="subtle">The title and message are written for you.</span>
            <button
              type="button"
              className="primary"
              disabled={preset === null || busy !== null}
              onClick={() => {
                if (preset === "swap") setStep("amount");
                else if (preset) void requestPreview({ userId, preset });
              }}
            >
              {busy === "preview" ? "Loading..." : "Continue"}
            </button>
          </div>
        </div>
      ) : null}

      {step === "amount" ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            if (swapBody) void requestPreview(swapBody);
          }}
        >
          <div className="swap-legs">
            <div className="swap-leg">
              <div className="swap-leg-row">
                <span className="field-label">Member sells</span>
                {sell ? (
                  <span className="field-count">
                    Balance {formatAmount(sell.balance)} {sell.symbol}
                  </span>
                ) : null}
              </div>
              <div className="swap-leg-row">
                <input
                  id="composer-amount"
                  aria-label={
                    denom === "fiat" ? "Amount in dollars" : `Amount in ${sell?.symbol ?? "tokens"}`
                  }
                  inputMode="decimal"
                  autoComplete="off"
                  className="swap-amount"
                  placeholder={denom === "fiat" ? "$0" : "0"}
                  value={denom === "fiat" && input !== "" ? `$${input}` : input}
                  onChange={(event) => setInput(cleanDecimal(event.target.value))}
                />
                <TokenButton
                  label="Sell token"
                  token={sell}
                  ambiguous={ambiguous}
                  onClick={() => setPicking("sell")}
                />
              </div>
              <div className="swap-leg-row">
                <span className="subtle">
                  {denom === "fiat"
                    ? `${amount ? formatAmount(amount) : "0"} ${sell?.symbol ?? ""}`
                    : sell?.price && amountValid
                      ? formatUsd(Number(amount) * sell.price)
                      : "$0.00"}
                </span>
                {sell?.price ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setDenomination(denom === "fiat" ? "token" : "fiat");
                      setInput("");
                    }}
                  >
                    Enter in {denom === "fiat" ? sell.symbol : "dollars"}
                  </button>
                ) : null}
              </div>
            </div>

            <div className="swap-leg">
              <div className="swap-leg-row">
                <span className="field-label">Member receives</span>
                {buy ? (
                  <span className="field-count">
                    Balance {formatAmount(buy.balance)} {buy.symbol}
                  </span>
                ) : null}
              </div>
              <div className="swap-leg-row">
                <span className="swap-amount swap-amount-out" aria-live="polite">
                  {quoteState === "loading"
                    ? "..."
                    : quote && buy
                      ? `~${formatBaseUnits(quote.toAmount, buy.decimals)}`
                      : "0"}
                </span>
                <TokenButton
                  label="Buy token"
                  token={buy}
                  ambiguous={ambiguous}
                  onClick={() => setPicking("buy")}
                />
              </div>
              <div className="swap-leg-row">
                <span className="subtle">
                  {quote && quote.toAmountUsd !== null ? formatUsd(quote.toAmountUsd) : "$0.00"}
                </span>
              </div>
            </div>
          </div>

          <p className="subtle">
            {quoteState === "failed"
              ? "No estimate right now. You can still preview and send."
              : "Estimate only, to check the amount. The member gets a fresh quote when they open the swap."}
          </p>

          {shortfall && sell ? (
            <ShortfallNotice symbol={sell.symbol} balance={sell.balance} amount={amount} />
          ) : null}
          <ErrorNotice error={error} />

          <div className="composer-actions">
            <button type="button" disabled={busy !== null} onClick={() => setStep("screen")}>
              Back
            </button>
            <span className="composer-actions-end">
              <button
                type="button"
                className="link-button"
                disabled={busy !== null}
                onClick={() => void requestPreview({ userId, preset: "swap" })}
              >
                Skip, open swap empty
              </button>
              <button type="submit" className="primary" disabled={!swapBody || busy !== null}>
                {busy === "preview" ? "Loading..." : "Continue"}
              </button>
            </span>
          </div>

          {picking ? (
            <TokenPicker
              title={picking === "sell" ? "Token the member sells" : "Token the member receives"}
              tokens={tokens}
              selectedKey={picking === "sell" ? sellKey : buyKey}
              ambiguous={ambiguous}
              onPick={(key) => pick(picking, key)}
              onClose={() => setPicking(null)}
            />
          ) : null}
        </form>
      ) : null}

      {step === "preview" && preview && previewed ? (
        <div className="stack">
          <div className="preview-frame">
            <div className="preview-card">
              <div className="preview-title">{preview.title}</div>
              <div className="preview-body">{preview.body}</div>
              <div className="preview-cta">{preview.ctaLabel}</div>
            </div>
          </div>
          <dl className="composer-meta">
            <dt>Opens</dt>
            <dd>{previewed.prefill ? `${screenLabel}, filled in` : screenLabel}</dd>
            {previewed.prefill && sell && buy ? (
              <>
                <dt>Sells</dt>
                <dd>
                  {previewed.prefill.amount} {sell.symbol}
                </dd>
                <dt>Receives</dt>
                <dd>{buy.symbol}</dd>
              </>
            ) : null}
          </dl>
          {previewed.prefill && shortfall && sell ? (
            <ShortfallNotice
              symbol={sell.symbol}
              balance={sell.balance}
              amount={previewed.prefill.amount}
            />
          ) : null}
          <ErrorNotice error={error} />
          <div className="composer-actions">
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => setStep(previewed.prefill ? "amount" : "screen")}
            >
              Back
            </button>
            <span className="composer-actions-end">
              <span className="subtle">Nothing is sent until you press Send.</span>
              <button
                type="button"
                className="primary"
                disabled={busy !== null}
                onClick={() => void send()}
              >
                {busy === "send" ? "Sending..." : "Send notification"}
              </button>
            </span>
          </div>
        </div>
      ) : null}

      {step === "sent" ? (
        <div className="stack">
          <div className="notice notice-ok" role="status">
            Sent: &ldquo;{preview?.title}&rdquo;. The API accepted it.
          </div>
          <div className="composer-actions">
            <span />
            <button type="button" onClick={startOver}>
              Send another
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TokenButton({
  label,
  token,
  ambiguous,
  onClick,
}: {
  label: string;
  token: PickerToken | null;
  ambiguous: Set<string>;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="token-button"
      aria-label={token ? `${label}: ${token.symbol}` : label}
      onClick={onClick}
    >
      {token ? (
        <>
          <TokenLogo url={token.logoUrl} symbol={token.symbol} />
          <span>
            {token.symbol}
            {ambiguous.has(token.symbol) ? (
              <span className="subtle"> {token.chainName}</span>
            ) : null}
          </span>
        </>
      ) : (
        <span>Choose</span>
      )}
      <span className="token-button-chevron" aria-hidden="true">
        ▾
      </span>
    </button>
  );
}

function ShortfallNotice({
  symbol,
  balance,
  amount,
}: {
  symbol: string;
  balance: string;
  amount: string;
}) {
  return (
    <div className="notice notice-warn" role="status">
      The member holds {formatAmount(balance)} {symbol}, less than {amount}. You can still send
      it; the app will show &ldquo;Not enough {symbol}&rdquo; until they have enough.
    </div>
  );
}

/**
 * Symbols that appear on more than one chain. The catalog is canonical today
 * (one token per symbol), so the picker names no network; should a symbol ever
 * repeat, those rows alone carry the chain, so two identical rows cannot appear.
 */
function ambiguousSymbols(tokens: PickerToken[]): Set<string> {
  const seen = new Set<string>();
  const repeated = new Set<string>();
  for (const t of tokens) {
    if (seen.has(t.symbol)) repeated.add(t.symbol);
    seen.add(t.symbol);
  }
  return repeated;
}

/** Digits and one point, whatever was typed or pasted ("$1,000.5" → "1000.5"). */
function cleanDecimal(raw: string): string {
  const [whole = "", ...rest] = raw.replace(/[^\d.]/g, "").split(".");
  return rest.length ? `${whole}.${rest.join("")}` : whole;
}

/** A plain positive decimal with no more places than the token has. */
function validAmount(amount: string, decimals: number): boolean {
  if (!/^\d+(\.\d+)?$/.test(amount) || Number(amount) <= 0) return false;
  return (amount.split(".")[1] ?? "").length <= decimals;
}
